import { useCallback } from 'react'
import * as Sentry from '@sentry/react'

/**
 * useSentryFeedback
 * Opens the Sentry feedback widget if present. Falls back to creating and opening the form
 * via the SDK when auto-injection is disabled. Returns a boolean indicating whether the widget
 * was successfully opened.
 */
export function useSentryFeedback(targetId: string = 'sentry-feedback') {
  const openFeedback = useCallback(async (): Promise<boolean> => {
    // Try clicking the injected floating widget's trigger button
    const container = typeof document !== 'undefined' ? document.getElementById(targetId) : null
    const trigger = container?.querySelector('button') as HTMLButtonElement | null
    if (trigger) {
      try {
        trigger.click()
        return true
      } catch {}
    }

    // Fallback: Use SDK to create and open the form
    const feedback = (Sentry as any)?.getFeedback?.()
    if (feedback?.createForm) {
      try {
        const form = await feedback.createForm()
        form?.appendToDom?.()
        form?.open?.()
        return true
      } catch {}
    }

    // Could not open via widget or SDK
    return false
  }, [targetId])

  return { openFeedback }
}
